import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { cleanBarcode } from 'app/common/cleanBarcode';

/**
 * Context untuk menyediakan status scanner barcode global dan fungsi dispatch ke seluruh aplikasi.
 */
const BarcodeScannerContext = createContext({
  activeFeatureInfo: '',
  isListening: false,
  lastScanned: null,
  isScanning: false,
  dispatchScan: () => {},
  registerSubscriber: () => () => {},
});

/**
 * Provider Barcode Scanner Global.
 * Menangkap ketukan keyboard scanner hardware secara global (capture phase) pada window,
 * mengumpulkan buffer karakter, membersihkan barcode, dan meneruskannya ke subscriber yang sedang aktif.
 *
 * @param {object} props - Properti komponen provider.
 * @param {React.ReactNode} props.children - Komponen turunan yang dibungkus oleh provider.
 * @returns {JSX.Element} Provider context pembungkus aplikasi.
 * @sideEffects
 * - Mendaftarkan listener 'keydown' global (capture phase) pada window.
 * - Mendaftarkan listener 'global-barcode-scan' untuk integrasi event eksternal/kamera.
 * - Mengatur timeout buffer debounce pemindaian.
 */
export function BarcodeScannerProvider({ children }) {
  const [activeFeatureInfo, setActiveFeatureInfo] = useState('');
  const [lastScanned, setLastScanned] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Stack daftar subscriber yang sedang aktif (halaman/modal)
  const subscribersRef = useRef([]);
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const isScanningTimeoutRef = useRef(null);

  /**
   * Mendaftarkan fungsi callback listener pemindaian barcode dari halaman atau modal.
   *
   * @param {Function} callback - Fungsi penangan barcode: (barcode: string) => void.
   * @param {object} [options] - Opsi konfigurasi subscriber.
   * @param {string} [options.info=''] - Keterangan nama fitur/halaman yang aktif.
   * @returns {Function} Fungsi cleanup untuk membatalkan pendaftaran subscriber (unsubscribe).
   * @sideEffects Memperbarui subscribersRef dan state activeFeatureInfo.
   */
  const registerSubscriber = useCallback((callback, options = {}) => {
    const subscriberId = Symbol('barcode_subscriber');
    const subscriber = {
      id: subscriberId,
      callback,
      info: options.info || '',
    };

    // Tambahkan subscriber ke urutan paling atas (stack: LIFO)
    subscribersRef.current = [subscriber, ...subscribersRef.current];
    setActiveFeatureInfo(options.info || '');

    return () => {
      subscribersRef.current = subscribersRef.current.filter((s) => s.id !== subscriberId);
      const nextActive = subscribersRef.current[0];
      setActiveFeatureInfo(nextActive ? nextActive.info : '');
    };
  }, []);

  /**
   * Memproses barcode bersih dan mendistribusikannya ke subscriber aktif teratas,
   * serta memicu efek visual indikator pemindaian.
   *
   * @param {string} rawBarcode - Nilai mentah barcode.
   * @sideEffects
   * - Menjalankan callback subscriber aktif teratas.
   * - Mengubah state lastScanned dan isScanning (visual flash).
   */
  const handleBarcodeProcess = useCallback((rawBarcode) => {
    const cleaned = cleanBarcode(rawBarcode);
    if (!cleaned) return;

    // Trigger visual feedback singkat pada indikator Topbar
    setLastScanned(cleaned);
    setIsScanning(true);
    if (isScanningTimeoutRef.current) clearTimeout(isScanningTimeoutRef.current);
    isScanningTimeoutRef.current = setTimeout(() => {
      setIsScanning(false);
    }, 600);

    // Kirim barcode ke subscriber teratas yang sedang aktif
    const activeSubscriber = subscribersRef.current[0];
    if (activeSubscriber && typeof activeSubscriber.callback === 'function') {
      try {
        activeSubscriber.callback(cleaned);
      } catch (err) {
        console.error('❌ ERROR onResult Barcode Scanner subscriber:', err);
      }
    }
  }, []);

  /**
   * Mengirim barcode secara manual ke subscriber aktif (misal dari hasil scan kamera / file upload).
   *
   * @param {string} barcode - Barcode yang akan didispatch.
   * @sideEffects Menjalankan handleBarcodeProcess.
   */
  const dispatchScan = useCallback((barcode) => {
    handleBarcodeProcess(barcode);
  }, [handleBarcodeProcess]);

  useEffect(() => {
    /**
     * Menangani event keydown global (capture phase) untuk barcode scanner.
     * Mengabaikan ketikan saat kursor berada di dalam elemen form input biasa,
     * dan menangkap ketikan beruntun scanner hardware saat fokus di luar form.
     *
     * @param {KeyboardEvent} e - Event keydown dari window.
     */
    const handleKeyDown = (e) => {
      const target = e.target || document.activeElement;
      const tag = target?.tagName?.toUpperCase();

      // Abaikan ketikan jika pengguna sedang aktif mengetik di elemen form biasa (input, textarea, select).
      // Pengecualian diberikan jika elemen form tersebut secara eksplisit mengizinkan pemindaian global
      // melalui atribut `data-allow-global-scan` (format boolean, "true", atau "1").
      const isFormElement =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        Boolean(target?.isContentEditable);

      const allowsGlobalScan =
        (target?.dataset?.allowGlobalScan !== undefined &&
          target?.dataset?.allowGlobalScan !== 'false') ||
        (typeof target?.hasAttribute === 'function' &&
          (target.hasAttribute('data-allow-global-scan') ||
            target.hasAttribute('data-allowglobalscan')) &&
          target.getAttribute('data-allow-global-scan') !== 'false' &&
          target.getAttribute('data-allowglobalscan') !== 'false');

      if (isFormElement && !allowsGlobalScan) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        bufferRef.current = '';
        return;
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Abaikan tombol modifier
      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'Meta' ||
        e.key === 'CapsLock'
      ) {
        return;
      }

      // Abaikan tombol kontrol khusus selain Enter
      if (e.key.length > 1 && e.key !== 'Enter') {
        bufferRef.current = '';
        return;
      }

      // Scanner hardware umumnya mengirimkan tombol Enter pada akhir pembacaan
      if (e.key === 'Enter') {
        if (bufferRef.current.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          handleBarcodeProcess(bufferRef.current);
          bufferRef.current = '';
        }
        return;
      }

      // Kumpulkan karakter ke dalam buffer scanner
      bufferRef.current += e.key;

      if (bufferRef.current.length > 255) {
        bufferRef.current = '';
        return;
      }

      // Fallback debounce timeout (300ms) untuk scanner hardware yang tidak mengirimkan karakter Enter
      timeoutRef.current = setTimeout(() => {
        if (bufferRef.current.length > 3) {
          handleBarcodeProcess(bufferRef.current);
        }
        bufferRef.current = '';
      }, 300);
    };

    /**
     * Menangani custom event 'global-barcode-scan' jika ada komponen yang memicunya lewat DOM.
     *
     * @param {CustomEvent} e - Custom event pembawa detail string barcode.
     */
    const handleGlobalBarcode = (e) => {
      if (e.detail) {
        handleBarcodeProcess(e.detail);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('global-barcode-scan', handleGlobalBarcode);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('global-barcode-scan', handleGlobalBarcode);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (isScanningTimeoutRef.current) clearTimeout(isScanningTimeoutRef.current);
      bufferRef.current = '';
    };
  }, [handleBarcodeProcess]);

  const value = {
    activeFeatureInfo,
    isListening: subscribersRef.current.length > 0,
    lastScanned,
    isScanning,
    dispatchScan,
    registerSubscriber,
  };

  return (
    <BarcodeScannerContext.Provider value={value}>
      {children}
    </BarcodeScannerContext.Provider>
  );
}

/**
 * Hook custom untuk mendaftarkan callback pemindaian barcode pada komponen atau halaman.
 * Callback akan otomatis di-unmount ketika komponen tidak aktif.
 *
 * @param {Function} onScan - Callback yang dipanggil saat barcode berhasil dipindai: (barcode: string) => void.
 * @param {object} [options] - Pengaturan pendaftaran scanner.
 * @param {string} [options.info=''] - Keterangan nama fitur/halaman (mis. "Sales Order", "Transfer Gudang").
 * @param {boolean} [options.active=true] - Status aktif listener untuk halaman/modal ini.
 * @sideEffects Mendaftarkan dan membatalkan pendaftaran subscriber ke BarcodeScannerContext.
 */
export function useBarcodeScanner(onScan, options = {}) {
  const { registerSubscriber } = useContext(BarcodeScannerContext);
  const { info = '', active = true } = options;
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!active || typeof registerSubscriber !== 'function') return;

    const unregister = registerSubscriber((barcode) => {
      if (onScanRef.current) {
        onScanRef.current(barcode);
      }
    }, { info });

    return unregister;
  }, [registerSubscriber, active, info]);
}

/**
 * Hook custom untuk membaca status barcode scanner global (misalnya untuk Topbar / indikator status).
 *
 * @returns {object} Objek context barcode scanner.
 */
export function useBarcodeScannerStatus() {
  return useContext(BarcodeScannerContext);
}

export default BarcodeScannerContext;
